import { useEffect, useRef, useState } from 'react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { DecodeHintType, BarcodeFormat } from '@zxing/library';

/**
 * 📷 Regal-QR-Dauerscanner (nur dieses Gerät, Opt-in)
 *
 * Läuft während/nach der KI-Analyse und füllt NUR das Regalfach-Feld — gespeichert
 * wird nie automatisch. Bleibt nach einem Treffer AN: ein weiterer Code überschreibt
 * sofort. Haptik über navigator.vibrate — kein Plugin, kein APK.
 *
 * Erkennung: bevorzugt die NATIVE BarcodeDetector-Engine (Android/Chrome) — die ist
 * deutlich robuster/schneller als der JS-Decoder. zxing bleibt nur als Fallback.
 */

const SETTING_KEY = 'kiosk_qr_regal_scan';

export function isShelfQrScanEnabled() {
  try { return localStorage.getItem(SETTING_KEY) === '1'; } catch { return false; }
}
export function setShelfQrScanEnabled(on) {
  try { localStorage.setItem(SETTING_KEY, on ? '1' : '0'); } catch { /* */ }
}

const VIDEO_CONSTRAINTS = {
  audio: false,
  video: {
    facingMode: { ideal: 'environment' },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
};

// Fokus/Zoom nur setzen, wenn das Gerät sie MELDET — blind gesetzt wirft Chromium
// „UnknownError: setPhotoOptions failed" (dieselbe interne Foto-Options-Schiene).
function optimiereTrack(stream, setFokus) {
  try {
    const track = stream && stream.getVideoTracks && stream.getVideoTracks()[0];
    if (!track || !track.applyConstraints) return;
    let caps = {};
    try { caps = (track.getCapabilities && track.getCapabilities()) || {}; } catch { /* */ }

    const modi = Array.isArray(caps.focusMode) ? caps.focusMode : [];
    const wunsch = ['continuous', 'single-shot'].find((m) => modi.includes(m));
    if (wunsch) {
      Promise.resolve(track.applyConstraints({ advanced: [{ focusMode: wunsch }] }))
        .then(() => setFokus(wunsch === 'continuous' ? 'AF' : 'AF1'))
        .catch(() => { /* Gerät kann es doch nicht */ });
    } else {
      setFokus('—');
    }
  } catch { /* */ }
}

// zxing-Formate für den Fallback (Name wie beim nativen BarcodeDetector)
const ZX = {
  qr_code: BarcodeFormat.QR_CODE, code_128: BarcodeFormat.CODE_128, code_39: BarcodeFormat.CODE_39,
  code_93: BarcodeFormat.CODE_93, itf: BarcodeFormat.ITF, ean_13: BarcodeFormat.EAN_13, ean_8: BarcodeFormat.EAN_8,
  codabar: BarcodeFormat.CODABAR, data_matrix: BarcodeFormat.DATA_MATRIX, aztec: BarcodeFormat.AZTEC, pdf417: BarcodeFormat.PDF_417,
};

const ZX_REV = {
  [BarcodeFormat.QR_CODE]: 'qr_code',
  [BarcodeFormat.CODE_128]: 'code_128',
  [BarcodeFormat.CODE_39]: 'code_39',
  [BarcodeFormat.CODE_93]: 'code_93',
  [BarcodeFormat.ITF]: 'itf',
  [BarcodeFormat.EAN_13]: 'ean_13',
  [BarcodeFormat.EAN_8]: 'ean_8',
  [BarcodeFormat.CODABAR]: 'codabar',
  [BarcodeFormat.DATA_MATRIX]: 'data_matrix',
  [BarcodeFormat.AZTEC]: 'aztec',
  [BarcodeFormat.PDF_417]: 'pdf417',
};

const DEFAULT_SCAN_FORMATE = [
  'qr_code', 'code_128', 'code_39', 'code_93', 'codabar',
  'data_matrix', 'itf', 'ean_13', 'ean_8', 'aztec', 'pdf417',
];

/**
 * @param {string[]} [formats] Standard: QR-Codes UND 1D-/2D-Barcodes (Code 128, Code 39, EAN, ITF, Data Matrix …).
 * onCode(text, alleTexte, foto, vollFoto, alleMitFormat)
 * @param {boolean} [mitFoto] Beim Erkennen ein Foto des Pakets (JPEG, max. 1280 px, data:-URL) aus dem Kamerabild
 *   mitliefern — für PaketPilot und für Vinted Go zum Wiederfinden des Pakets im Regal (Wunsch Folkert).
 * @param {boolean} [eingebettet] Kamerabild groß an Ort und Stelle zeigen (in einem Dialog) statt als kleines
 *   Fenster unten rechts — für Abläufe, in denen der Scan die Hauptsache ist.
 * @param {boolean} [vollbildMoeglich] (nur eingebettet) Knopf ⛶ für Kamera im Vollbild (Wunsch Folkert 22.10.2026:
 *   „full screen camera when scanning", wie in der Vinted-App). Die Wahl merkt sich das Gerät (kiosk_scan_vollbild).
 * @param {React.ReactNode} [vollbildInfo] Im Vollbild unten über dem Kamerabild: z. B. die letzten Scans mit Name/Fach.
 */
const VOLLBILD_KEY = 'kiosk_scan_vollbild';
// Erkannten Code anzeigen: bei Vinted-Nummern (16-stellig „17…" oder „VGS…") den Vorspann klein und die
// entscheidende Kurznummer fett („1789835100 852-908"), wie die Vinted-App es tut; sonst den Code, wie er ist.
function anzeigeCode(code) {
  const c = String(code || '');
  if (/^(17\d{14}|VGS\d{6,}|JVGL[A-Z0-9]{6,}|VGL[A-Z0-9]{6,}|3S[A-Z0-9]{6,}|JUN[A-Z0-9]{8,}|JJN[A-Z0-9]{8,}|JJD[A-Z0-9]{8,}|0034\d{12,})$/i.test(c)) {
    const kurz = c.slice(-6);
    return <><span style={{ opacity: 0.6, fontSize: '0.85em' }}>{c.slice(0, -6)} </span><b style={{ fontSize: '1.15em' }}>{kurz.slice(0, 3)}-{kurz.slice(3)}</b></>;
  }
  const mDpd = c.replace(/^(\][A-Za-z]\d)+/, '').replace(/^%+/, '').replace(/[\s-]+/g, '').match(/^[0-9A-Z]{7}(\d{14})\d{5,6}[0-9A-Z]$/i);
  if (mDpd) {
    const nr = mDpd[1];
    return <><span style={{ opacity: 0.6, fontSize: '0.85em' }}>{nr.slice(0, 8)} </span><b style={{ fontSize: '1.15em' }}>{nr.slice(-6)}</b></>;
  }
  return c.length > 24 ? c.slice(0, 22) + '…' : c;
}

export default function ShelfQrScanner({ active, onCode, formats, filter, eingebettet = false, mitFoto = false, vollbildMoeglich = false, vollbildInfo = null }) {
  const [vollbild, setVollbildState] = useState(() => { try { return eingebettet && vollbildMoeglich && localStorage.getItem(VOLLBILD_KEY) === '1'; } catch { return false; } });
  const setVollbild = (on) => { setVollbildState(on); try { localStorage.setItem(VOLLBILD_KEY, on ? '1' : '0'); } catch { /* */ } };
  const wunschFormate = Array.isArray(formats) && formats.length ? formats : DEFAULT_SCAN_FORMATE;
  const formatKey = wunschFormate.join(',');
  const nurQr = formatKey === 'qr_code';
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const zxingControlsRef = useRef(null);
  const loopRef = useRef(null);
  const mountedRef = useRef(true);
  const lastRef = useRef({ code: '', at: 0 });
  // Callback in einer Ref: käme er in die Dependencies, würde eine Inline-Arrow aus dem
  // Parent die Kamera bei JEDEM Render neu starten.
  const onCodeRef = useRef(onCode);
  onCodeRef.current = onCode;
  const filterRef = useRef(filter);
  filterRef.current = filter;

  const [status, setStatus] = useState('start'); // start | live | error
  const [engine, setEngine] = useState('');      // 'nativ' | 'js'
  const [fokus, setFokus] = useState('');
  const [letzter, setLetzter] = useState('');

  useEffect(() => () => { mountedRef.current = false; }, []);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let retryTimer = null;
    mountedRef.current = true;

    // Schnappschuss aus dem laufenden Video (volles Kamerabild bis 1280 px, damit sowohl in PaketPilot als auch in
    // der Vinted-Go-App das ganze Paket statt nur eines QR-Ausschnitts zu sehen ist; Wunsch Folkert 25.10.2026).
    // Wichtig (29.10.2026, Folkert Ees Steijnen / Tess Mulder): Das Kamerabild wird VOR dem asynchronen
    // `await detector.detect(...)` in `frameCanvas` eingefroren. Sonst läuft das Live-Video während der 200–500 ms
    // Barcode-Erkennung weiter, und beim schnellen Wegziehen vom Stapel fotografiert `schnappschuss()` bereits das
    // darunterliegende nächste Paket.
    const frameCanvas = mitFoto ? document.createElement('canvas') : null;
    let frameReady = false;
    const merkeFrame = () => {
      frameReady = false;
      if (!frameCanvas) return;
      try {
        const v = videoRef.current;
        if (!v || !v.videoWidth || !v.videoHeight) return;
        const W = v.videoWidth, H = v.videoHeight;
        const maxKante = 1280;
        const scale = Math.min(1, maxKante / Math.max(W, H));
        const w = Math.round(W * scale), h = Math.round(H * scale);
        if (frameCanvas.width !== w) frameCanvas.width = w;
        if (frameCanvas.height !== h) frameCanvas.height = h;
        frameCanvas.getContext('2d').drawImage(v, 0, 0, W, H, 0, 0, w, h);
        frameReady = true;
      } catch { /* */ }
    };
    const schnappschuss = () => {
      try {
        if (frameReady && frameCanvas && frameCanvas.width && frameCanvas.height) {
          const vollFoto = frameCanvas.toDataURL('image/jpeg', 0.68);
          return { foto: vollFoto, vollFoto };
        }
        const v = videoRef.current;
        if (!v || !v.videoWidth || !v.videoHeight) return { foto: '', vollFoto: '' };
        const W = v.videoWidth, H = v.videoHeight;
        const maxKante = 1280;
        const scale = Math.min(1, maxKante / Math.max(W, H));
        const cVoll = document.createElement('canvas');
        cVoll.width = Math.round(W * scale);
        cVoll.height = Math.round(H * scale);
        cVoll.getContext('2d').drawImage(v, 0, 0, W, H, 0, 0, cVoll.width, cVoll.height);
        const vollFoto = cVoll.toDataURL('image/jpeg', 0.68);
        return { foto: vollFoto, vollFoto };
      } catch { return { foto: '', vollFoto: '' }; }
    };
    const treffer = (text, alle, box, alleMitFormat) => {
      const code = String(text || '').trim();
      if (!code || cancelled || !mountedRef.current) return;
      if (filterRef.current && !filterRef.current(code)) return;
      const now = Date.now();
      // Denselben Code nicht spammen; ein NEUER Code überschreibt sofort.
      if (code === lastRef.current.code && now - lastRef.current.at < 1500) return;
      lastRef.current = { code, at: now };
      setLetzter(code.slice(0, 40));
      try { if (navigator.vibrate) navigator.vibrate(60); } catch { /* */ }
      const { foto, vollFoto } = mitFoto ? schnappschuss(box) : { foto: '', vollFoto: '' };
      const alleListe = Array.isArray(alle) && alle.length ? alle : [code];
      const formatListe = Array.isArray(alleMitFormat) && alleMitFormat.length ? alleMitFormat : [{ text: code, format: '' }];
      if (onCodeRef.current) onCodeRef.current(code, alleListe, foto, vollFoto, formatListe);
    };

    // Bei mehreren Codes im selben Bild eindeutige Sendungsnummern (Vinted, DHL: JVGL/3S/JUN/JJD/0034, DPD 28-stellig/Aztec) vor 11-14-stelligen Händler-Barcodes bevorzugen
    const codeRang = (raw) => {
      const r = String(raw || '').trim();
      if (r.startsWith('[)>')) return 0;
      const s = r.replace(/\s+/g, '').replace(/^(\][A-Za-z]\d)+/, '').replace(/^\(([A-Z0-9]+)\)/i, '$1');
      if (/^(17\d{14}|VGS\d{6,}|JVGL[A-Z0-9]{6,}|VGL[A-Z0-9]{6,}|JJGL[A-Z0-9]{6,}|CWGL[A-Z0-9]{6,}|3S[A-Z0-9]{6,}|JUN[A-Z0-9]{8,}|IUN[A-Z0-9]{8,}|JJN[A-Z0-9]{8,}|JJD[A-Z0-9]{8,}|JD\d{12,}|0034\d{12,}|0038\d{12,}|[A-Z]{2}\d{9}[A-Z]{2}|%?[0-9A-Z]{7}\d{14}\d{5,6}[0-9A-Z])$/i.test(s)) return 0;
      if (/^\d{11,14}$/.test(s)) return 1;
      if (/^[A-Z0-9]{8,35}$/i.test(s) && !/^https?:/i.test(s)) return 2;
      return 3;
    };

    // ---- Weg A: native Plattform-Engine (bevorzugt) ----
    const startNativ = async () => {
      if (!window.BarcodeDetector) return false;
      try {
        let nutzbar = wunschFormate;
        if (window.BarcodeDetector.getSupportedFormats) {
          const formate = await window.BarcodeDetector.getSupportedFormats();
          nutzbar = wunschFormate.filter((f) => (formate || []).includes(f));
          if (!nutzbar.length) return false;
        }
        const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
        if (cancelled || !mountedRef.current) { stream.getTracks().forEach((t) => t.stop()); return true; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          try { await videoRef.current.play(); } catch { /* */ }
        }
        optimiereTrack(stream, (f) => { if (mountedRef.current) setFokus(f); });

        const detector = new window.BarcodeDetector({ formats: nutzbar });
        const tick = async () => {
          if (cancelled || !mountedRef.current) return;
          try {
            if (mitFoto) merkeFrame();
            const rawCodes = await detector.detect(videoRef.current);
            const passend = rawCodes && filterRef.current ? rawCodes.filter((c) => filterRef.current(c.rawValue)) : rawCodes;
            if (passend && passend.length) {
              const codes = [...passend].sort((a, b) => codeRang(a.rawValue) - codeRang(b.rawValue));
              const alleTexte = codes.map((c) => String(c.rawValue || '').trim()).filter(Boolean);
              const alleMitFormat = codes
                .map((c) => ({ text: String(c.rawValue || '').trim(), format: String(c.format || '').toLowerCase() }))
                .filter((c) => c.text);
              treffer(codes[0].rawValue, alleTexte, codes[0].boundingBox, alleMitFormat);
            }
          } catch { /* einzelner Frame-Fehler ist egal */ }
          loopRef.current = setTimeout(tick, 100);
        };
        tick();
        setEngine('nativ');
        setStatus('live');
        return true;
      } catch {
        return false; // → Fallback
      }
    };

    // ---- Weg B: zxing (JS-Decoder) als Fallback ----
    const startZxing = async () => {
      const hints = new Map();
      hints.set(DecodeHintType.TRY_HARDER, true);
      hints.set(DecodeHintType.POSSIBLE_FORMATS, wunschFormate.map((f) => ZX[f]).filter((f) => f !== undefined));
      const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 80 });
      const controls = await reader.decodeFromConstraints(VIDEO_CONSTRAINTS, videoRef.current, (result) => {
        if (result) {
          // Lage des Codes aus den Eckpunkten (zxing liefert keine Box)
          let box = null;
          try {
            const pts = (result.getResultPoints ? result.getResultPoints() : result.resultPoints) || [];
            const xs = pts.map((p) => (p.getX ? p.getX() : p.x)), ys = pts.map((p) => (p.getY ? p.getY() : p.y));
            if (xs.length >= 2) box = { x: Math.min(...xs), y: Math.min(...ys), width: Math.max(...xs) - Math.min(...xs), height: Math.max(...ys) - Math.min(...ys) };
          } catch { /* ohne Box: Bildmitte */ }
          const txt = result.getText ? result.getText() : result.text;
          const rawFmt = result.getBarcodeFormat ? result.getBarcodeFormat() : result.format;
          const fmt = ZX_REV[rawFmt] || String(rawFmt || '').toLowerCase();
          treffer(txt, [txt], box, [{ text: String(txt || '').trim(), format: fmt }]);
        }
      });
      if (cancelled || !mountedRef.current) { try { Promise.resolve(controls.stop()).catch(() => {}); } catch { /* */ } return; }
      zxingControlsRef.current = controls;
      try {
        const s = videoRef.current && videoRef.current.srcObject;
        optimiereTrack(s, (f) => { if (mountedRef.current) setFokus(f); });
      } catch { /* */ }
      setEngine('js');
      setStatus('live');
    };

    const start = async (versuch = 0) => {
      try {
        const okNativ = await startNativ();
        if (okNativ || cancelled || !mountedRef.current) return;
        await startZxing();
      } catch {
        if (cancelled || !mountedRef.current) return;
        if (versuch === 0) {
          // Kamera evtl. noch von der System-Kamera-App belegt → einmal nachfassen
          retryTimer = setTimeout(() => start(1), 500);
          return;
        }
        setStatus('error');
      }
    };

    const t = setTimeout(() => start(0), 0); // sofort (nur aus dem Render-Zyklus heraus)

    return () => {
      cancelled = true;
      clearTimeout(t);
      if (retryTimer) clearTimeout(retryTimer);
      if (loopRef.current) { clearTimeout(loopRef.current); loopRef.current = null; }
      try {
        if (zxingControlsRef.current) Promise.resolve(zxingControlsRef.current.stop()).catch(() => {});
      } catch { /* */ }
      zxingControlsRef.current = null;
      try {
        if (streamRef.current) streamRef.current.getTracks().forEach((tr) => tr.stop());
      } catch { /* */ }
      streamRef.current = null;
      if (videoRef.current) { try { videoRef.current.srcObject = null; } catch { /* */ } }
    };
  }, [active, formatKey]);

  if (!active) return null;

  const randfarbe = status === 'live' ? '#4caf50' : status === 'error' ? '#e5484d' : '#9e9e9e';
  const voll = eingebettet && vollbildMoeglich && vollbild;
  // Dasselbe <video>-Element in beiden Ansichten (nur der Rahmen ändert sich) → der Kamerastrom läuft beim Umschalten weiter
  return (
    <div data-testid="qr-scanner" data-eingebettet={eingebettet ? '1' : '0'} data-vollbild={voll ? '1' : '0'} style={voll ? {
      position: 'fixed', inset: 0, zIndex: 7700, background: '#000', display: 'flex', flexDirection: 'column',
      border: `3px solid ${randfarbe}`, boxSizing: 'border-box',
    } : eingebettet ? {
      position: 'relative', width: '100%', boxSizing: 'border-box',
      borderRadius: 10, overflow: 'hidden', background: '#000', border: `2px solid ${randfarbe}`,
    } : {
      position: 'fixed', right: 12, bottom: 12, zIndex: 7000, width: 150,
      borderRadius: 10, overflow: 'hidden', background: '#000',
      border: `2px solid ${randfarbe}`, boxShadow: '0 4px 14px rgba(0,0,0,0.35)',
    }}>
      {/* contain: du siehst GENAU den Ausschnitt, den der Decoder auswertet */}
      <video ref={videoRef} muted playsInline
        style={voll
          ? { width: '100%', flex: 1, minHeight: 0, objectFit: 'contain', display: 'block', background: '#000' }
          : { width: '100%', height: eingebettet ? 230 : 95, objectFit: 'contain', display: 'block', background: '#000' }} />
      {eingebettet && vollbildMoeglich && (
        <button type="button" data-testid="qr-vollbild" aria-label={voll ? 'Vollbild aus' : 'Vollbild'} onClick={() => setVollbild(!voll)}
          style={{ position: 'absolute', top: voll ? 'max(12px, env(safe-area-inset-top))' : 8, right: voll ? 12 : 8, width: voll ? 52 : 40, height: voll ? 52 : 40,
            borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: voll ? 26 : 20, cursor: 'pointer', zIndex: 1, lineHeight: 1 }}>
          {voll ? '✕' : '⛶'}
        </button>
      )}
      {voll && vollbildInfo && (
        <div data-testid="qr-vollbild-info" style={{ background: 'rgba(0,0,0,0.78)', color: '#fff', padding: '8px 12px', maxHeight: '38vh', overflowY: 'auto' }}>
          {vollbildInfo}
        </div>
      )}
      <div style={{ fontSize: voll ? 16 : eingebettet ? 13 : 10, color: '#fff', background: 'rgba(0,0,0,0.65)', padding: voll ? '10px 8px max(10px, env(safe-area-inset-bottom))' : eingebettet ? '5px 6px' : '3px 4px', textAlign: 'center' }}>
        {status === 'error'
          ? '⚠️ keine Kamera'
          : letzter
            ? <span>✓ {anzeigeCode(letzter)}</span>
            : `🔍 ${nurQr ? 'QR' : 'Code'}…${engine ? ' ' + engine : ''}${fokus ? '/' + fokus : ''}`}
      </div>
    </div>
  );
}
